import type { Company } from '@jaf/contracts';
import { type ReactNode, useMemo, useState } from 'react';
import { Button } from '@/shared/ui/primitives/button';
import { Checkbox } from '@/shared/ui/primitives/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/primitives/dialog';
import { Label } from '@/shared/ui/primitives/label';
import { scopeOptions } from './company-scope';

/** Props do diálogo de seleção personalizada. */
export interface CustomScopeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Empresas que o usuário pode ver. */
  companies: readonly Company[];
  /** Empresas já marcadas ao abrir. */
  initialCompanyIds: readonly string[];
  onConfirm: (companyIds: string[]) => void;
}

/**
 * Seleção personalizada do escopo: marca as empresas a consolidar, entre as permitidas, na
 * mesma ordem do seletor (cada matriz seguida das filiais).
 */
export function CustomScopeDialog({
  open,
  onOpenChange,
  companies,
  initialCompanyIds,
  onConfirm,
}: CustomScopeDialogProps): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Seleção personalizada</DialogTitle>
          <DialogDescription>
            Marque as empresas que entram nas telas e relatórios.
          </DialogDescription>
        </DialogHeader>
        {open && (
          <CompanyChecklist
            companies={companies}
            initialCompanyIds={initialCompanyIds}
            onCancel={() => {
              onOpenChange(false);
            }}
            onConfirm={onConfirm}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface CompanyChecklistProps {
  companies: readonly Company[];
  initialCompanyIds: readonly string[];
  onCancel: () => void;
  onConfirm: (companyIds: string[]) => void;
}

function CompanyChecklist({
  companies,
  initialCompanyIds,
  onCancel,
  onConfirm,
}: CompanyChecklistProps): ReactNode {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set(initialCompanyIds));
  const options = useMemo(
    () =>
      scopeOptions(companies).flatMap((option) =>
        option.scope.kind === 'COMPANY' ? [{ ...option, companyId: option.scope.companyId }] : [],
      ),
    [companies],
  );
  const toggle = (companyId: string, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(companyId);
      else next.delete(companyId);
      return next;
    });
  };

  return (
    <>
      <ul
        aria-label="Empresas da seleção"
        className="max-h-72 space-y-2 overflow-y-auto rounded-md border p-3"
      >
        {options.map((option) => {
          const id = `custom-scope-${option.companyId}`;
          return (
            <li key={option.companyId} className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={selected.has(option.companyId)}
                onCheckedChange={(checked) => {
                  toggle(option.companyId, checked === true);
                }}
              />
              <Label htmlFor={id} className="font-normal">
                {option.label}
              </Label>
            </li>
          );
        })}
      </ul>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          disabled={selected.size === 0}
          onClick={() => {
            onConfirm(
              options
                .map((option) => option.companyId)
                .filter((companyId) => selected.has(companyId)),
            );
          }}
        >
          Aplicar
        </Button>
      </DialogFooter>
    </>
  );
}
